import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      nav: {
        signIn: 'Sign In',
        getStarted: 'Get Started',
        tips: 'Tips & Guides',
        language: 'Language',
        dashboard: 'Dashboard',
        messages: 'Messages',
        wallet: 'Wallet',
        postJob: 'Post a Job',
        logout: 'Log Out',
      },
      landing: {
        heroTitle: 'Where Pros Get Work Done',
        heroSubtitle: 'A performance-based marketplace connecting homeowners with licensed, insured contractors.',
        postJob: 'Post a Job',
        becomePro: 'Become a Pro',
        howItWorks: 'How It Works',
        categoriesTitle: 'Browse by Category',
      },
      common: {
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        back: 'Back',
        next: 'Next',
        submit: 'Submit',
        readMore: 'Read More',
        helpful: 'Helpful',
        search: 'Search',
        viewAll: 'View All',
        continue: 'Continue',
        or: 'or',
        and: 'and',
      },
      tips: {
        title: 'Construction Tips & Guides',
        subtitle: 'AI-powered expert advice for every home project',
        allCategories: 'All Categories',
        noResults: 'No tips found in this category yet.',
        keyPoints: 'Key Points',
        whenToCallPro: 'When to Call a Professional',
        costEstimate: 'Estimated Cost',
        introduction: 'Overview',
        views: 'views',
        markHelpful: 'Mark as helpful',
        thanks: 'Thanks for your feedback!',
        backToTips: 'Back to all tips',
        relatedArticles: 'Related articles',
      },
      auth: {
        email: 'Email',
        password: 'Password',
        login: 'Log In',
        register: 'Create Account',
        iAmA: 'I am a',
        client: 'Client / Homeowner',
        contractor: 'Contractor',
        continueWithGoogle: 'Continue with Google',
        finishingSignIn: 'Finishing sign-in…',
        signInFailed: 'Sign-in failed. Please try again.',
        phone: 'Phone',
        address: 'Street Address',
        state: 'State',
        zipCode: 'ZIP Code',
        licenseNumber: 'Contractor License #',
        licenseHelp: 'Your state-issued contractor license number. We verify this before your profile goes live.',
        optional: 'optional',
        consentTermsPrefix: 'I agree to the',
        consentTermsLink: 'Terms of Use',
        consentPrivacyLink: 'Privacy Policy',
        consentSmsTermsLink: 'SMS Terms',
        consentTransactional: 'By checking this box, I consent to receive transactional messages from ContractorsLab.com related to my account, such as account creation confirmations, job match alerts, proposal notifications, and account status updates.',
        consentMarketing: 'By checking this box, I agree to receive marketing messages from ContractorsLab.com, including promotional offers and updates.',
        smsDisclaimer1: 'Consent to receive SMS messages is optional and not a condition for using ContractorsLab.com services. Message and data rates may apply. You may receive up to 4 messages per month. You can opt out at any time by texting STOP. For assistance, text HELP or contact us at',
        smsDisclaimer2: 'Carriers are not responsible for delayed or undelivered messages. All information collected during the SMS opt-in process will not be sold, rented, or shared with third parties for their own marketing use. View our',
        errTerms: 'You must agree to the Terms of Use and Privacy Policy to continue.',
        errSmsConsent: 'You must consent to receive transactional account messages to create your account.',
      },
      dashboard: {
        welcome: 'Welcome back',
        yourJobs: 'Your Jobs',
        activeJobs: 'Active Jobs',
        completedJobs: 'Completed',
        totalJobs: 'Total',
        noJobs: 'No jobs yet. Post your first job to get matched with pros.',
        newJob: 'New Job',
        yourLeads: 'Your Leads',
        yourProposals: 'Your Proposals',
        wonJobs: 'Won Jobs',
        pendingContactRequests: 'Pending Contact Requests',
        quickStats: 'Quick Stats',
        leadsReceived: 'Leads Received',
        proposalsSent: 'Proposals Sent',
        wins: 'Wins',
        qualityScore: 'Quality Score',
      },
      wallet: {
        title: 'Wallet',
        balance: 'Current Balance',
        minBalance: 'Minimum Balance',
        topUp: 'Top Up',
        topUpAmount: 'Top-Up Amount',
        transactions: 'Transaction History',
        noTransactions: 'No transactions yet.',
      },
      messages: {
        title: 'Messages',
        typeMessage: 'Type a message…',
        send: 'Send',
        requestContact: 'Request Contact Reveal',
        approveReveal: 'Approve Contact Reveal',
        rejectReveal: 'Reject',
        contactRevealed: 'Contact info has been shared.',
        contactPending: 'Contact request pending…',
        noMessages: 'No messages yet. Start the conversation!',
      },
      legal: {
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
        smsTerms: 'SMS Terms',
        lastUpdated: 'Last Updated',
        backHome: 'Back to Home',
      },
    },
  },
  es: {
    translation: {
      nav: {
        signIn: 'Iniciar Sesión',
        getStarted: 'Comenzar',
        tips: 'Consejos y Guías',
        language: 'Idioma',
        dashboard: 'Panel',
        messages: 'Mensajes',
        wallet: 'Billetera',
        postJob: 'Publicar Trabajo',
        logout: 'Cerrar Sesión',
      },
      landing: {
        heroTitle: 'Donde los Profesionales Hacen el Trabajo',
        heroSubtitle: 'Un mercado basado en desempeño que conecta propietarios con contratistas licenciados y asegurados.',
        postJob: 'Publicar Trabajo',
        becomePro: 'Ser Profesional',
        howItWorks: 'Cómo Funciona',
        categoriesTitle: 'Explorar por Categoría',
      },
      common: {
        loading: 'Cargando...',
        save: 'Guardar',
        cancel: 'Cancelar',
        back: 'Atrás',
        next: 'Siguiente',
        submit: 'Enviar',
        readMore: 'Leer Más',
        helpful: 'Útil',
        search: 'Buscar',
        viewAll: 'Ver Todo',
        continue: 'Continuar',
        or: 'o',
        and: 'y',
      },
      tips: {
        title: 'Consejos y Guías de Construcción',
        subtitle: 'Consejos expertos con IA para cada proyecto del hogar',
        allCategories: 'Todas las Categorías',
        noResults: 'No hay consejos en esta categoría aún.',
        keyPoints: 'Puntos Clave',
        whenToCallPro: 'Cuándo Llamar a un Profesional',
        costEstimate: 'Costo Estimado',
        introduction: 'Resumen',
        views: 'vistas',
        markHelpful: 'Marcar como útil',
        thanks: '¡Gracias por tu comentario!',
        backToTips: 'Volver a todos los consejos',
        relatedArticles: 'Artículos relacionados',
      },
      auth: {
        email: 'Correo Electrónico',
        password: 'Contraseña',
        login: 'Entrar',
        register: 'Crear Cuenta',
        iAmA: 'Soy un',
        client: 'Cliente / Propietario',
        contractor: 'Contratista',
        continueWithGoogle: 'Continuar con Google',
        finishingSignIn: 'Finalizando inicio de sesión…',
        signInFailed: 'Inicio de sesión fallido. Inténtalo de nuevo.',
        phone: 'Teléfono',
        address: 'Dirección',
        state: 'Estado',
        zipCode: 'Código Postal',
        licenseNumber: 'Licencia de Contratista #',
        licenseHelp: 'Número de licencia de contratista emitido por tu estado. Lo verificamos antes de activar tu perfil.',
        optional: 'opcional',
        consentTermsPrefix: 'Acepto los',
        consentTermsLink: 'Términos de Uso',
        consentPrivacyLink: 'Política de Privacidad',
        consentSmsTermsLink: 'Términos de SMS',
        consentTransactional: 'Al marcar esta casilla, doy mi consentimiento para recibir mensajes transaccionales de ContractorsLab.com relacionados con mi cuenta, como confirmaciones de creación de cuenta, alertas de coincidencia de trabajos, notificaciones de propuestas y actualizaciones del estado de la cuenta.',
        consentMarketing: 'Al marcar esta casilla, acepto recibir mensajes de marketing de ContractorsLab.com, incluidas ofertas promocionales y actualizaciones.',
        smsDisclaimer1: 'El consentimiento para recibir mensajes SMS es opcional y no es condición para usar los servicios de ContractorsLab.com. Pueden aplicarse tarifas de mensajes y datos. Puede recibir hasta 4 mensajes por mes. Puede cancelar en cualquier momento enviando STOP. Para asistencia, envíe HELP o contáctenos en',
        smsDisclaimer2: 'Los operadores no son responsables de mensajes retrasados o no entregados. Toda la información recopilada durante el proceso de suscripción por SMS no se venderá, alquilará ni compartirá con terceros para su propio uso de marketing. Consulte nuestros',
        errTerms: 'Debes aceptar los Términos de Uso y la Política de Privacidad para continuar.',
        errSmsConsent: 'Debes dar tu consentimiento para recibir mensajes transaccionales de la cuenta para crear tu cuenta.',
      },
      dashboard: {
        welcome: 'Bienvenido',
        yourJobs: 'Tus Trabajos',
        activeJobs: 'Trabajos Activos',
        completedJobs: 'Completados',
        totalJobs: 'Total',
        noJobs: 'Aún no hay trabajos. Publica tu primer trabajo para encontrar profesionales.',
        newJob: 'Nuevo Trabajo',
        yourLeads: 'Tus Oportunidades',
        yourProposals: 'Tus Propuestas',
        wonJobs: 'Trabajos Ganados',
        pendingContactRequests: 'Solicitudes de Contacto Pendientes',
        quickStats: 'Estadísticas Rápidas',
        leadsReceived: 'Oportunidades Recibidas',
        proposalsSent: 'Propuestas Enviadas',
        wins: 'Ganadas',
        qualityScore: 'Puntaje de Calidad',
      },
      wallet: {
        title: 'Billetera',
        balance: 'Saldo Actual',
        minBalance: 'Saldo Mínimo',
        topUp: 'Recargar',
        topUpAmount: 'Monto de Recarga',
        transactions: 'Historial de Transacciones',
        noTransactions: 'Aún no hay transacciones.',
      },
      messages: {
        title: 'Mensajes',
        typeMessage: 'Escribe un mensaje…',
        send: 'Enviar',
        requestContact: 'Solicitar Revelar Contacto',
        approveReveal: 'Aprobar Revelar Contacto',
        rejectReveal: 'Rechazar',
        contactRevealed: 'La información de contacto ha sido compartida.',
        contactPending: 'Solicitud de contacto pendiente…',
        noMessages: 'Aún no hay mensajes. ¡Inicia la conversación!',
      },
      legal: {
        terms: 'Términos de Servicio',
        privacy: 'Política de Privacidad',
        smsTerms: 'Términos de SMS',
        lastUpdated: 'Última Actualización',
        backHome: 'Volver al Inicio',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'clab_lang',
      caches: ['localStorage'],
    },
  });

export default i18n;
